// src/components/layout/Sidebar.js
// ✅ VERSÃO FINAL - Desligar como item de menu normal
import React, { useMemo, useState, useEffect } from 'react';
import { useAppContext } from '../../context/AppContext';
import { apiFetch } from '../../apiFetch';
import { sendWebSocketMessage } from '../../services/websocketSingleton';
import { tokens, spacing, fontSize, borderRadius, transitions } from '../../theme/tokens';
import WskIndicator from '../shared/WskIndicator';
import SpotIndicator from '../shared/SpotIndicator';

const STORE_GROUP_MAX_LABEL = 20;

function normalizeSidebarLabel(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function buildStoreGroupLabel(usuario) {
  const fullLabel = normalizeSidebarLabel(usuario?.nomeCurto)
    || normalizeSidebarLabel(usuario?.nome)
    || 'Loja';

  if (fullLabel.length <= STORE_GROUP_MAX_LABEL) {
    return { label: fullLabel, title: fullLabel };
  }

  return {
    label: `${fullLabel.slice(0, STORE_GROUP_MAX_LABEL - 3)}...`,
    title: fullLabel,
  };
}

function buildMenuItems(storeGroup) {
  return [
    {
      id: 'radio',
      label: 'Rádio',
      color: 'var(--action)',
      children: [
        { id: 'cadastro', label: 'Configurações', screen: 'cadastro' },
      ]
    },
    {
      id: 'programacao',
      label: 'Programação',
      color: 'var(--action)',
      children: [
        { id: 'resultado', label: 'Spots',   screen: 'resultado' },
        { id: 'eventos',   label: 'Eventos', screen: 'eventos' }
      ]
    },
    {
      id: 'conteudo',
      label: 'Conteúdo',
      color: 'var(--action)',
      children: [
        { id: 'locutor', label: 'Locutor', component: 'LocutorControl' },
        { id: 'locutorMonitor', label: 'Monitor Locutor', component: 'LocutorMonitor' }
      ]
    },
    {
      id: 'loja-operacao',
      label: storeGroup.label,
      title: storeGroup.title,
      color: 'var(--action)',
      children: [
        { id: 'storeMap',    label: 'Mapa da Loja',         component: 'StoreMapControl', produtos: ['indoor'] },
        { id: 'semanticReview', label: 'Revisão Semântica', component: 'SemanticReview',   produtos: ['indoor'] },
        { id: 'pesoSecoes',  label: 'Peso por Seção',       component: 'PesoSecoes',      produtos: ['indoor'] }
      ]
    },
    {
      id: 'sistema',
      label: 'Sistema',
      color: 'var(--error)',
      children: [
        { id: 'systemSettings', label: 'Preferências e aplicativos', component: 'Sistema' },
        { id: 'clock',    label: 'Relógio',           action: 'clock' },
        { id: 'download', label: 'Baixar Radio Player', action: 'download', produtos: ['indoor'] },
        { id: 'logout',   label: 'Sair',               action: 'logout' },
        { id: 'shutdown', label: 'Desligar',            action: 'shutdown' }
      ]
    }
  ];
}

function Sidebar({ currentScreen, onNavigate, collapsed, onToggleCollapse, spotQueue, clearQueue, onShowClock }) {
  const { usuario } = useAppContext() || {};
  const tipoProduto = usuario?.tipoProduto || 'indoor';
  const storeGroup = useMemo(() => buildStoreGroupLabel(usuario), [usuario?.nome, usuario?.nomeCurto]);
  const menuItems = useMemo(() => buildMenuItems(storeGroup), [storeGroup]);
  const [expandedGroups, setExpandedGroups] = useState(['radio', 'programacao', 'conteudo', 'loja-operacao']);

  const itemVisivel = (item) => {
    if (!item.produtos) return true;
    return item.produtos.includes(tipoProduto);
  };
  const [isShuttingDown, setIsShuttingDown] = useState(false);

  const [, forceUpdate] = useState(0)
  useEffect(() => {
    const observer = new MutationObserver(() => forceUpdate(n => n + 1))
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    })
    return () => observer.disconnect()
  }, [])

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  useEffect(() => {
    if (collapsed) return;

    const activeGroup = menuItems.find(group =>
      group.children.some(item => currentScreen === item.screen || currentScreen === item.component)
    );

    if (!activeGroup) return;

    setExpandedGroups(prev =>
      prev.includes(activeGroup.id) ? prev : [...prev, activeGroup.id]
    );
  }, [collapsed, currentScreen, menuItems]);

  // ============================================
  // ✅ FUNÇÃO DE SHUTDOWN
  // ============================================
  const handleShutdown = async () => {
    const confirmShutdown = window.confirm(
      '⚠️ Desligar iafm.io?\n\n' +
      'Isso irá:\n' +
      '• Desconectar todos os clientes WebSocket\n' +
      '• Parar o servidor backend\n' +
      '• Você precisará reiniciar manualmente\n\n' +
      'Deseja continuar?'
    );

    if (!confirmShutdown) return;

    setIsShuttingDown(true);

    try {
      // Desconectar WebSockets
      await apiFetch('/api/websocket/disconnect-all', { method: 'POST' });

      await new Promise(resolve => setTimeout(resolve, 1000));

      // Shutdown do servidor
      await apiFetch('/api/system/shutdown', { method: 'POST' });

      alert(
        '✅ iafm.io foi desligado!\n\n' +
        'O servidor backend parou.\n' +
        'Feche esta aba e execute start-iafm.bat para reiniciar.'
      );

      setTimeout(() => {
        window.close();
      }, 3000);

    } catch (error) {
      console.error('Erro no shutdown:', error);
      alert(
        '⚠️ Erro ao desligar!\n\n' +
        'Use o stop-iafm.bat manualmente para parar o sistema.'
      );
    } finally {
      setIsShuttingDown(false);
    }
  };

  const handleLogout = async () => {
    const isIndoor = (usuario?.tipoProduto || '') === 'indoor';
    if (isIndoor) {
      const pararPlaylist = window.confirm('Deseja parar a playlist também?');
      if (pararPlaylist) {
        sendWebSocketMessage('PLAYER_PAUSE');
        await new Promise(r => setTimeout(r, 500));
      }
    }
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch {}
    localStorage.removeItem('radio-ai-jwt');
    localStorage.removeItem('radio-ai-user');
    window.location.href = '/login';
  };

  const handleItemClick = (item) => {
    if (item.action === 'clock') {
      onShowClock && onShowClock();
      return;
    }
    if (item.action === 'download') {
      window.open('/downloads/radio-player-indoor-setup.exe', '_blank');
      return;
    }
    // ✅ Se for ação de shutdown
    if (item.action === 'shutdown') {
      handleShutdown();
      return;
    }
    if (item.action === 'logout') {
      handleLogout();
      return;
    }
    
    // Navegação normal
    if (item.screen) {
      onNavigate(item.screen);
    } else if (item.component) {
      onNavigate(item.component);
    }
  };

  const isActive = (item) => {
    return currentScreen === item.screen || currentScreen === item.component;
  };

  return (
    <div style={{
      position: 'sticky',
      top: 0,
      height: '100%',
      width: collapsed ? '60px' : '260px',
      minWidth: collapsed ? '60px' : '260px',
      flexShrink: 0,
      background: tokens.bgSurface(),
      boxShadow: `1px 0 0 0 ${tokens.bgOverlay()}`,
      transition: transitions.base,
      zIndex: 998, // Abaixo dos overlays globais
      display: 'flex',
      flexDirection: 'column',
      overflowX: 'hidden',
      overflowY: 'auto'
    }}>
      {/* Header */}
      <div style={{
        padding: collapsed ? spacing.md : spacing.xl,
        borderBottom: `1px solid ${tokens.bgOverlay()}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: '70px'
      }}>
        {!collapsed && (
          <div>
            <h2 style={{
              margin: 0,
              fontSize: fontSize['2xl'],
              fontWeight: 'bold',
              background: tokens.gradientBrand(),
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              iafm.io
            </h2>
          </div>
        )}
        
        <button
          onClick={onToggleCollapse}
          style={{
            background: 'transparent',
            border: 'none',
            width: '32px',
            height: '32px',
            borderRadius: borderRadius.md,
            color: tokens.textPrimary(),
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: fontSize.lg,
            transition: transitions.all,
            marginLeft: collapsed ? 'auto' : 0,
            marginRight: collapsed ? 'auto' : 0
          }}
          onMouseEnter={e => e.currentTarget.style.background = tokens.bgSurface()}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d={collapsed ? 'M4 2l5 4-5 4' : 'M8 2L3 6l5 4'}
              stroke="#94a3b8"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Menu Items */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: spacing.sm,
        display: 'flex',
        flexDirection: 'column'
      }}>
        {menuItems.filter(group => group.children.filter(itemVisivel).length > 0).map(group => (
          <div key={group.id} style={{ marginBottom: spacing.sm }}>
            {/* Group Header */}
            <button
              onClick={() => !collapsed && toggleGroup(group.id)}
              style={{
                width: '100%',
                background: 'transparent',
                border: 'none',
                padding: spacing.md,
                borderRadius: borderRadius.md,
                color: tokens.textPrimary(),
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'space-between',
                fontSize: fontSize.base,
                fontWeight: 'semibold',
                transition: transitions.all,
                position: 'relative'
              }}
              onMouseEnter={e => e.currentTarget.style.background = tokens.bgSurface()}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              title={group.title || (collapsed ? group.label : '')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md }}>
                {collapsed
                  ? <span style={{ fontSize: fontSize.xs, fontWeight: 700, letterSpacing: 1 }}>{group.label.slice(0, 2).toUpperCase()}</span>
                  : <span>{group.label}</span>
                }
              </div>
              {!collapsed && (
                <span style={{
                  fontSize: fontSize.sm,
                  transition: transitions.all,
                  transform: expandedGroups.includes(group.id) ? 'rotate(90deg)' : 'rotate(0deg)'
                }}>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M2 3l3 3 3-3"
                      stroke="#94a3b8"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              )}
            </button>

            {/* Group Children */}
            {!collapsed && expandedGroups.includes(group.id) && (
              <div style={{
                marginTop: spacing.xs,
                marginLeft: spacing.lg
              }}>
                {group.children.filter(itemVisivel).map(item => (
                  <button
                    key={item.id}
                    onClick={() => handleItemClick(item)}
                    disabled={item.action === 'shutdown' && isShuttingDown}
                    style={{
                      width: '100%',
                      background: isActive(item) ? 'var(--action-subtle)' : 'transparent',
                      border: isActive(item) ? `1px solid ${group.color}` : 'none',
                      padding: `${spacing.sm} ${spacing.md}`,
                      borderRadius: borderRadius.md,
                      color: isActive(item) ? group.color : tokens.textSecondary(),
                      cursor: (item.action === 'shutdown' && isShuttingDown) ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: spacing.md,
                      fontSize: fontSize.sm,
                      transition: transitions.all,
                      marginBottom: spacing.xs,
                      textAlign: 'left',
                      opacity: (item.action === 'shutdown' && isShuttingDown) ? 0.5 : 1
                    }}
                    onMouseEnter={e => {
                      if (!isActive(item) && !(item.action === 'shutdown' && isShuttingDown)) {
                        e.currentTarget.style.background = 'var(--action-subtle)';
                        e.currentTarget.style.color = tokens.textPrimary();
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isActive(item)) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = tokens.textSecondary();
                      }
                    }}
                  >
                    <span>
                      {item.action === 'shutdown' && isShuttingDown ? 'Desligando...' : item.label}
                    </span>
                  </button>
                ))}
                {group.id === 'sistema' && (
                  <div style={{ marginTop: spacing.xs }}>
                    <SpotIndicator
                      spotQueue={spotQueue}
                      clearQueue={clearQueue}
                      collapsed={collapsed}
                    />
                    <WskIndicator collapsed={collapsed} />
                  </div>
                )}
              </div>
            )}

            {/* Collapsed tooltips */}
            {collapsed && (
              <div style={{
                marginTop: spacing.xs,
                display: 'flex',
                flexDirection: 'column',
                gap: spacing.xs,
                alignItems: 'center'
              }}>
                {group.children.filter(itemVisivel).map(item => (
                  <button
                    key={item.id}
                    onClick={() => handleItemClick(item)}
                    disabled={item.action === 'shutdown' && isShuttingDown}
                    title={item.action === 'shutdown' && isShuttingDown ? 'Desligando...' : item.label}
                    style={{
                      background: isActive(item) ? 'var(--action-subtle)' : 'transparent',
                      border: isActive(item) ? `1px solid ${group.color}` : 'none',
                      width: '40px',
                      height: '40px',
                      borderRadius: borderRadius.md,
                      color: isActive(item) ? group.color : tokens.textSecondary(),
                      cursor: (item.action === 'shutdown' && isShuttingDown) ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: fontSize.lg,
                      transition: transitions.all,
                      opacity: (item.action === 'shutdown' && isShuttingDown) ? 0.5 : 1
                    }}
                    onMouseEnter={e => {
                      if (!isActive(item) && !(item.action === 'shutdown' && isShuttingDown)) {
                        e.currentTarget.style.background = 'var(--action-subtle)';
                      }
                    }}
                    onMouseLeave={e => {
                      if (!isActive(item)) {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    <span style={{ fontSize: fontSize.xs, fontWeight: 700, letterSpacing: 0.5 }}>
                      {item.action === 'shutdown' && isShuttingDown ? '…' : item.label.slice(0, 2).toUpperCase()}
                    </span>
                  </button>
                ))}
                {group.id === 'sistema' && (
                  <>
                    <SpotIndicator
                      spotQueue={spotQueue}
                      clearQueue={clearQueue}
                      collapsed={collapsed}
                    />
                    <WskIndicator collapsed={collapsed} />
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Footer */}
      {!collapsed && (
        <div style={{
          padding: spacing.lg,
          borderTop: `1px solid ${tokens.bgOverlay()}`,
          fontSize: fontSize.xs,
          color: tokens.textMuted(),
          textAlign: 'center'
        }}>
          <div>v1.0.0 • {new Date().getFullYear()}</div>
        </div>
      )}
    </div>
  );
}

export default Sidebar;
